/**
 * Medical Leaves List Page
 * List and manage Medical Leaves (Licenças Médicas)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao } from '../../authorization/useVeredito';
import { medicalLeaveService } from '../../services/medicalLeaveService';
import {
  MedicalLeave,
  MedicalLeaveFilters,
  formatDate,
  formatCrm,
} from '../../types/medicalLeave';
import {
  MedicalLeavesFiltersComponent,
  MedicalLeavesFormDialog,
  CertificateViewer,
} from './components';
import { LeaveFiltersData } from './components/MedicalLeavesFilters';

const columns: DataTableColumn<MedicalLeave>[] = [
  {
    id: 'employeeName',
    label: 'Funcionário',
    primary: true,
    render: (medicalLeave) => (
      <Typography variant="body2" fontWeight="medium">
        {medicalLeave.employeeName}
      </Typography>
    ),
  },
  {
    id: 'companyName',
    label: 'Empresa',
    render: (medicalLeave) => (
      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
        {medicalLeave.companyName}
      </Typography>
    ),
  },
  {
    id: 'departmentName',
    label: 'Setor',
    render: (medicalLeave) => (
      <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
        {medicalLeave.departmentName}
      </Typography>
    ),
  },
  {
    id: 'crm',
    label: 'CRM',
    render: (medicalLeave) => (
      <Typography variant="body2" fontFamily="monospace">
        {formatCrm(medicalLeave.doctorCrm, medicalLeave.doctorUf)}
      </Typography>
    ),
  },
  {
    id: 'cidCode',
    label: 'CID',
    render: (medicalLeave) => (
      <Chip label={medicalLeave.cidCode} size="small" color="primary" variant="outlined" />
    ),
  },
  {
    id: 'cidDescription',
    label: 'Descrição',
    render: (medicalLeave) => (
      <Tooltip title={medicalLeave.cidDescription}>
        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
          {medicalLeave.cidDescription}
        </Typography>
      </Tooltip>
    ),
  },
  {
    id: 'startDate',
    label: 'Início',
    render: (medicalLeave) => (
      <Typography variant="body2">{formatDate(medicalLeave.startDate)}</Typography>
    ),
  },
  {
    id: 'endDate',
    label: 'Fim',
    render: (medicalLeave) => (
      <Typography variant="body2">{formatDate(medicalLeave.endDate)}</Typography>
    ),
  },
  {
    id: 'totalDays',
    label: 'Dias',
    render: (medicalLeave) => (
      <Chip label={`${medicalLeave.totalDays} dias`} size="small" color="info" />
    ),
  },
];

export const MedicalLeavesListPage: React.FC = () => {
  const verAtestado = useAcao('medical_leaves.read', 'Ver Atestado');
  const editar = useAcao('medical_leaves.update', 'Editar');
  const excluir = useAcao('medical_leaves.delete', 'Excluir');
  const [medicalLeaves, setMedicalLeaves] = useState<MedicalLeave[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<MedicalLeaveFilters>({});

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMedicalLeave, setSelectedMedicalLeave] = useState<MedicalLeave | null>(null);
  const [certificateViewerOpen, setCertificateViewerOpen] = useState(false);
  const [viewingMedicalLeave, setViewingMedicalLeave] = useState<MedicalLeave | null>(null);

  const loadMedicalLeaves = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await medicalLeaveService.getMedicalLeaves(params);
      setMedicalLeaves(response.medicalLeaves);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao carregar licenças médicas');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadMedicalLeaves();
  }, [loadMedicalLeaves]);

  // Convert compact filter UI data → API filters
  const handleFilter = useCallback((data: LeaveFiltersData) => {
    const filters: MedicalLeaveFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.startDate) filters.startDate = data.startDate;
    if (data.endDate) filters.endDate = data.endDate;

    setApiFilters(filters);
    setPage(0);
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta licença médica?')) {
      return;
    }

    try {
      await medicalLeaveService.deleteMedicalLeave(id);
      loadMedicalLeaves();
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao excluir licença médica');
    }
  };

  const handleEdit = (medicalLeave: MedicalLeave) => {
    setSelectedMedicalLeave(medicalLeave);
    setEditDialogOpen(true);
  };

  const handleViewCertificate = (medicalLeave: MedicalLeave) => {
    setViewingMedicalLeave(medicalLeave);
    setCertificateViewerOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedMedicalLeave(null);
  };

  const handleEditSuccess = () => {
    loadMedicalLeaves();
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Licenças Médicas' },
      ]}
      title="Licenças Médicas"
      subtitle="Gerencie as licenças médicas dos funcionários"
      actions={
        <RouteActionButton
          para="/medical-leaves/apply"
          exige={pode('medical_leaves.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Aplicar Licença
        </RouteActionButton>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters - compact inline */}
      <Box sx={{ mb: 3 }}>
        <MedicalLeavesFiltersComponent
          onFilter={handleFilter}
          loading={loading}
          total={total}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={medicalLeaves}
        getRowId={(medicalLeave) => medicalLeave.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma licença médica encontrada',
          description:
            'As licenças aparecem aqui depois de aplicadas a um funcionário, com o atestado anexado.',
        }}
        actions={[
          {
            icon: <AttachFileIcon fontSize="small" />,
            color: 'info',
            onClick: handleViewCertificate,
            hidden: (medicalLeave) => !medicalLeave.hasCertificate,
            ...verAtestado,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (medicalLeave) => handleDelete(medicalLeave.id),
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      {/* Edit Dialog */}
      <MedicalLeavesFormDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        onSuccess={handleEditSuccess}
        medicalLeave={selectedMedicalLeave}
      />

      {/* Certificate Viewer */}
      <CertificateViewer
        open={certificateViewerOpen}
        onClose={() => setCertificateViewerOpen(false)}
        medicalLeave={viewingMedicalLeave}
      />
    </PageShell>
  );
};

export default MedicalLeavesListPage;
